/**
 * Quote Quick Edit: the client for /api/quotes/artifacts.
 *
 * Four routes, four functions, and nothing else. The backend contract lives
 * in backend/src/routes/quote-artifacts.js; this file is only the wire.
 *
 * TWO THINGS THAT BITE HERE, both handled once so no caller has to remember:
 *
 *   1. apiFetch converts response keys snake_case -> camelCase at the TOP
 *      level. So the row arrives as { quoteRef, totalExGst, createdAt, ... }.
 *      `quote_data` and `form_data` are on the OPAQUE_JSONB_KEYS list in
 *      src/services/data-service-api.js, which means their KEY is camelised to
 *      quoteData / formData but their VALUE passes through byte-for-byte. That
 *      is exactly what recovery needs: the snapshot has to come back as it was
 *      written or it is not a snapshot.
 *
 *   2. quote_artifacts.total_ex_gst is NUMERIC(14,2) (migration 085), and
 *      node-postgres returns numeric as a STRING to avoid silent float damage.
 *      So the field arrives as "12345.67", not 12345.67. Every read goes
 *      through moneyOrNull below, which returns null for absent and NEVER 0.
 *      A missing total and a total of zero are different facts on a record of
 *      what a customer was charged.
 */

import { apiFetch } from '../data-service-api.js';
import { normaliseQuoteRef } from './artifact-stamp.js';

/** Row shape returned by the LIST surface (no quote_data, no form_data). */
export interface QuoteArtifactSummary {
    id: string;
    tenantId: string;
    /** null when the project was deleted. The artefact outlives it by design. */
    projectId: string | null;
    quoteRef: string;
    projectTitle: string | null;
    clientName: string | null;
    /** null when no total was recorded. Never 0 as a stand-in for unknown. */
    totalExGst: number | null;
    gstInclusive: boolean | null;
    filename: string | null;
    appVersion: string | null;
    /** ISO 8601. */
    createdAt: string;
    createdBy: string | null;
}

/** The full artefact, as returned by GET /by-ref/:ref. */
export interface QuoteArtifactRecord extends QuoteArtifactSummary {
    /**
     * The quote exactly as rendered. Typed unknown on purpose: its shape is
     * owned by the quote editor, not by this table, and hard-typing it here
     * would make every editor change a schema change.
     */
    quoteData: unknown;
    formData: unknown;
}

export interface CreateQuoteArtifactInput {
    quoteRef: string;
    quoteData: Record<string, unknown>;
    formData?: Record<string, unknown> | null;
    projectId?: string | null;
    projectTitle?: string | null;
    clientName?: string | null;
    totalExGst?: number | null;
    gstInclusive?: boolean | null;
    filename?: string | null;
    appVersion?: string | null;
}

const ARTIFACTS_PATH = '/quotes/artifacts';

function textOrNull(value: unknown): string | null {
    if (value === null || value === undefined) return null;
    const text = String(value).trim();
    return text ? text : null;
}

/**
 * NUMERIC arrives as a string. Returns null for absent or unparseable, never a
 * fallback figure. See the header note.
 */
function moneyOrNull(value: unknown): number | null {
    if (value === null || value === undefined || value === '') return null;
    const num = typeof value === 'number' ? value : Number(String(value).replace(/[$,\s]/g, ''));
    return Number.isFinite(num) ? num : null;
}

/** Tri-state: true / false / not recorded. Absence is not false. */
function booleanOrNull(value: unknown): boolean | null {
    if (value === null || value === undefined || value === '') return null;
    if (typeof value === 'boolean') return value;
    if (value === 'true' || value === 1 || value === '1') return true;
    if (value === 'false' || value === 0 || value === '0') return false;
    return null;
}

function toSummary(row: Record<string, unknown>): QuoteArtifactSummary {
    return {
        id: String(row.id ?? ''),
        tenantId: String(row.tenantId ?? ''),
        projectId: textOrNull(row.projectId),
        quoteRef: String(row.quoteRef ?? ''),
        projectTitle: textOrNull(row.projectTitle),
        clientName: textOrNull(row.clientName),
        totalExGst: moneyOrNull(row.totalExGst),
        gstInclusive: booleanOrNull(row.gstInclusive),
        filename: textOrNull(row.filename),
        appVersion: textOrNull(row.appVersion),
        createdAt: String(row.createdAt ?? ''),
        createdBy: textOrNull(row.createdBy),
    };
}

function asRecord(value: unknown): Record<string, unknown> {
    return value && typeof value === 'object' && !Array.isArray(value)
        ? (value as Record<string, unknown>)
        : {};
}

/**
 * Every artefact for the tenant, newest first. Pass projectId to scope it to
 * one project.
 *
 * The list deliberately carries no quote_data, so it stays small enough to
 * render a whole book of quotes. Hydrate the one the operator picked with
 * getQuoteArtifactByRef.
 */
export async function listQuoteArtifacts(
    options: { projectId?: string | null } = {},
): Promise<QuoteArtifactSummary[]> {
    const projectId = textOrNull(options.projectId);
    const path = projectId
        ? `${ARTIFACTS_PATH}?projectId=${encodeURIComponent(projectId)}`
        : ARTIFACTS_PATH;
    const body = asRecord(await apiFetch(path));
    const rows = Array.isArray(body.rows) ? body.rows : [];
    return rows.map((row: unknown) => toSummary(asRecord(row)));
}

/**
 * Fetch one artefact verbatim.
 *
 * Returns null for 404 rather than throwing, because "this PDF carries a
 * reference we have no record of" is an ANSWER the confirmation step has to
 * show the operator (it means falling back to reading the document), not a
 * failure. Any other status still throws: a 500 must not be reported to the
 * operator as "no such quote".
 */
export async function getQuoteArtifactByRef(ref: string): Promise<QuoteArtifactRecord | null> {
    // Normalise through the one shared definition of the reference format.
    // A ref read off a PDF text layer can arrive lower-case or with the group
    // separators mangled, and the column comparison is exact.
    const normalised = normaliseQuoteRef(ref);
    if (!normalised) return null;

    try {
        const row = asRecord(await apiFetch(`${ARTIFACTS_PATH}/by-ref/${encodeURIComponent(normalised)}`));
        return {
            ...toSummary(row),
            quoteData: row.quoteData ?? null,
            formData: row.formData ?? null,
        };
    } catch (err) {
        if ((err as { status?: number } | null)?.status === 404) return null;
        throw err;
    }
}

/** Body of POST /api/quotes/artifacts/sent. The backend builds every hash. */
export interface RecordSentQuoteInput {
    /** Minted once per send confirmation; the retry idempotency key. */
    quoteRef: string;
    projectId: string;
    /** The quote exactly as Stage 5 rendered it, inline media removed. */
    quoteData: Record<string, unknown>;
    formData?: Record<string, unknown> | null;
    sentConfirmedAt: string;
    draftOpenedAt?: string | null;
    delivery: {
        mode?: string | null;
        to: string;
        cc?: string | null;
        subject?: string | null;
        pdfFilename?: string | null;
    };
    corpus?: {
        jobType?: string | null;
        summary?: string | null;
        workTypeTags?: string[];
        flags?: string[];
    };
    appVersion?: string | null;
}

export interface RecordSentQuoteResult {
    artifactId: string;
    quoteRef: string;
    packageHash: string;
    deliveryEventId: string;
    corpusEntryId: string | null;
    corpusStatus: string | null;
    /** Set when the send was recorded but the corpus could not take it. */
    corpusSkippedReason: string | null;
    supersededCorpusEntryIds: string[];
    /** false when this was a retry of a send already recorded. */
    created: boolean;
}

/**
 * Record a confirmed send: the sent-quote snapshot, its delivery event and the
 * corpus row, in one backend transaction. Idempotent on quoteRef, so callers
 * retry with the identical body until it lands.
 */
export async function recordSentQuote(input: RecordSentQuoteInput): Promise<RecordSentQuoteResult> {
    const row = asRecord(await apiFetch(`${ARTIFACTS_PATH}/sent`, {
        method: 'POST',
        body: JSON.stringify(input),
    }));
    return {
        artifactId: String(row.artifactId ?? ''),
        quoteRef: String(row.quoteRef ?? ''),
        packageHash: String(row.packageHash ?? ''),
        deliveryEventId: String(row.deliveryEventId ?? ''),
        corpusEntryId: textOrNull(row.corpusEntryId),
        corpusStatus: textOrNull(row.corpusStatus),
        corpusSkippedReason: textOrNull(row.corpusSkippedReason),
        supersededCorpusEntryIds: Array.isArray(row.supersededCorpusEntryIds)
            ? row.supersededCorpusEntryIds.map(String)
            : [],
        created: row.created === true,
    };
}

/**
 * Record an artefact at the moment a quote PDF is produced.
 *
 * Idempotent on (tenant_id, quote_ref): a repeat POST returns the ORIGINAL row
 * unchanged rather than overwriting it, because the artefact is the record of
 * what was sent.
 *
 * Caveat worth knowing: the backend distinguishes "written" (201) from
 * "already existed" (200), but apiFetch resolves to the parsed body and drops
 * the status, so this function cannot report which happened. If a caller ever
 * needs that distinction, compare the returned createdAt against now rather
 * than adding a second fetch path here.
 */
export async function createQuoteArtifact(
    input: CreateQuoteArtifactInput,
): Promise<QuoteArtifactRecord> {
    const row = asRecord(await apiFetch(ARTIFACTS_PATH, {
        method: 'POST',
        body: JSON.stringify(input),
    }));
    return {
        ...toSummary(row),
        quoteData: row.quoteData ?? null,
        formData: row.formData ?? null,
    };
}
